import React, { useState, KeyboardEvent, useRef, useCallback, useEffect } from 'react';
import { Send, Image, X, Paperclip, FileText, Sparkles } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase/config';
import { toast } from 'react-hot-toast';
import { typingIndicatorService } from '../../services/typingIndicatorService';
import { ChatAttachment } from '../../services/chat';
import GifPicker from './GifPicker';

interface MessageInputProps {
  onSend: (text: string, imageUrl?: string, attachment?: ChatAttachment) => void;
  compact?: boolean;
  chatId?: string;
  currentUser?: any;
  placeholder?: string;
  autoFocus?: boolean;
}

const MessageInput: React.FC<MessageInputProps> = ({ onSend, compact = false, chatId, currentUser, placeholder, autoFocus }) => {
  const [text, setText] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedGif, setSelectedGif] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const keepAliveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const minTextareaHeight = compact ? 32 : 42;
  const maxTextareaHeight = compact ? 160 : 420;

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
  }, [autoFocus, chatId]);

  // Cleanup typing indicator on unmount
  useEffect(() => {
    return () => {
      if (chatId && currentUser?.uid) {
        typingIndicatorService.stopTyping(chatId, currentUser.uid);
      }
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
      }
    };
  }, [chatId, currentUser]);

  // Reset textarea height when text is cleared
  useEffect(() => {
    if (!text && textareaRef.current) {
      textareaRef.current.style.height = `${minTextareaHeight}px`;
    }
  }, [text, compact, minTextareaHeight]);

  // Typing indicator handlers
  const handleTypingStart = useCallback(async (draftContent?: string) => {
    if (chatId && currentUser?.uid && currentUser?.email) {
      const displayName = currentUser.displayName || currentUser.email;
      await typingIndicatorService.startTyping(chatId, currentUser.uid, currentUser.email, displayName, draftContent);
    }
  }, [chatId, currentUser]);

  const handleTypingStop = useCallback(async () => {
    if (chatId && currentUser?.uid) {
      await typingIndicatorService.stopTyping(chatId, currentUser.uid);
    }
    // Clear keep-alive interval
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
  }, [chatId, currentUser]);

  const handleTypingUpdate = useCallback(async (draftContent: string) => {
    if (chatId && currentUser?.uid) {
      await typingIndicatorService.updateTypingActivity(chatId, currentUser.uid, draftContent);
    }
  }, [chatId, currentUser]);

  // Handle focus - start typing indicator and keep-alive
  const handleFocus = useCallback(async () => {
    setIsFocused(true);
    
    // Start typing indicator with current text
    await handleTypingStart(text);
    
    // Set up keep-alive interval to prevent timeout while focused
    // Update typing activity every 2 seconds to keep it alive
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
    }
    
    keepAliveIntervalRef.current = setInterval(async () => {
      if (chatId && currentUser?.uid) {
        await typingIndicatorService.updateTypingActivity(chatId, currentUser.uid, text);
      }
    }, 10000); // Update every 10 seconds to keep the indicator alive
  }, [chatId, currentUser, text, handleTypingStart]);

  // Handle blur - stop typing indicator
  const handleBlur = useCallback(async () => {
    setIsFocused(false);
    
    // Stop typing indicator when field loses focus
    await handleTypingStop();
  }, [handleTypingStop]);

  // Auto-resize textarea
  const autoResizeTextarea = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, maxTextareaHeight)}px`;
    }
  }, [maxTextareaHeight]);

  const handleTextChange = useCallback((value: string) => {
    console.log('⌨️ Text change:', { 
      oldValue: text, 
      newValue: value, 
      chatId, 
      userId: currentUser?.uid,
      userEmail: currentUser?.email 
    });
    
    setText(value);
    
    // Auto-resize textarea
    setTimeout(autoResizeTextarea, 0);
    
    // Start typing indicator when user starts typing
    if (value.trim() && !text.trim()) {
      console.log('🟢 Starting typing indicator with content:', value);
      handleTypingStart(value);
    }
    // Update typing indicator with current content (character-by-character)
    else if (value.trim()) {
      console.log('🔄 Updating typing indicator with content:', value);
      handleTypingUpdate(value);
    }
    // Stop typing indicator when input is cleared
    else if (!value.trim() && text.trim()) {
      console.log('🔴 Stopping typing indicator');
      handleTypingStop();
    }
  }, [text, handleTypingStart, handleTypingStop, handleTypingUpdate, autoResizeTextarea]);

  const handleSend = async () => {
    if (!text.trim() && !selectedImage && !selectedFile && !selectedGif) return;

    setIsUploading(true);
    try {
      let imageUrl = '';
      let attachment: ChatAttachment | undefined;
      
      if (selectedImage) {
        // Upload image to Firebase Storage
        const timestamp = Date.now();
        const fileName = `chat-images/${timestamp}-${selectedImage.name}`;
        const storageRef = ref(storage, fileName);
        
        const snapshot = await uploadBytes(storageRef, selectedImage);
        imageUrl = await getDownloadURL(snapshot.ref);
      } else if (selectedGif) {
        // Use GIF URL directly
        imageUrl = selectedGif;
      }

      if (selectedFile) {
        // Upload file to Firebase Storage
        const timestamp = Date.now();
        const fileName = `chat-files/${timestamp}-${selectedFile.name}`;
        const storageRef = ref(storage, fileName);
        
        const snapshot = await uploadBytes(storageRef, selectedFile);
        const fileUrl = await getDownloadURL(snapshot.ref);
        
        attachment = {
          url: fileUrl,
          fileName: selectedFile.name,
          fileSize: selectedFile.size,
          fileType: selectedFile.type,
          uploadPath: fileName
        };
      }

      // Stop typing indicator before sending
      await handleTypingStop();

      // Send the message with text, image, and/or file attachment
      onSend(text.trim() || '', imageUrl, attachment);
      
      // Clear inputs
      setText('');
      setSelectedImage(null);
      setImagePreview('');
      setSelectedFile(null);
      setSelectedGif('');
      
      // Keep focus on textarea for quick follow-up messages
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isUploading) {
      e.preventDefault();
      handleSend();
    }
    // Allow Shift+Enter for line breaks (default textarea behavior)
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    // Check if clipboard contains an image
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault(); // Prevent default paste behavior for images
        
        const blob = item.getAsFile();
        if (blob) {
          // Create a File object from the blob
          const timestamp = Date.now();
          const file = new File([blob], `pasted-image-${timestamp}.png`, { type: blob.type });
          
          setSelectedImage(file);
          
          // Create preview URL
          const reader = new FileReader();
          reader.onload = (e) => {
            setImagePreview(e.target?.result as string);
          };
          reader.readAsDataURL(file);
          
          toast.success('Image pasted! Ready to send.');
        }
        break;
      }
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Check if it's an image
    if (file.type.startsWith('image/')) {
      setSelectedImage(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      
      toast.success('Image added! Ready to send.');
    } else {
      toast.error('Please drop an image file');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File must be smaller than 10MB');
        return;
      }

      // Validate file type (allow PDFs and common document types)
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];

      if (!allowedTypes.includes(file.type)) {
        toast.error('Only PDF, Word, Excel, and text files are allowed');
        return;
      }

      setSelectedFile(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview('');
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleGifSelect = (gifUrl: string) => {
    setSelectedGif(gifUrl);
    setShowGifPicker(false);
  };

  const removeGif = () => {
    setSelectedGif('');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const hasContent = !!(text.trim() || selectedImage || selectedFile || selectedGif);

  if (compact) {
    const thumb = (src: string, onRemove: () => void, label?: string) => (
      <div className="relative h-16 w-16 flex-shrink-0">
        <img src={src} alt="" className="h-full w-full rounded-lg object-cover border border-gray-200" />
        {label && <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 text-[9px] font-semibold text-white">{label}</span>}
        <button
          onClick={onRemove}
          className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-gray-900 text-white hover:bg-black"
          title="Remove"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );

    return (
      <div className="px-3 pb-3 pt-2">
        {(imagePreview || selectedGif || selectedFile) && (
          <div className="mb-2 flex items-center gap-2">
            {imagePreview && thumb(imagePreview, removeImage)}
            {selectedGif && thumb(selectedGif, removeGif, 'GIF')}
            {selectedFile && (
              <div className="flex min-w-0 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 py-1.5 pl-2 pr-1">
                <FileText className="h-4 w-4 flex-shrink-0 text-gray-500" />
                <span className="min-w-0 truncate text-xs text-gray-800">{selectedFile.name}</span>
                <span className="flex-shrink-0 text-[11px] text-gray-400">{formatFileSize(selectedFile.size)}</span>
                <button onClick={removeFile} className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700" title="Remove">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        )}
        <div
          className={`flex items-end gap-0.5 rounded-[22px] border bg-white py-1 pl-4 pr-1 transition-colors ${
            isDragging ? 'border-gray-900 bg-gray-50' : isFocused ? 'border-gray-400' : 'border-gray-200'
          }`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => handleTextChange(e.target.value)}
            onKeyPress={handleKeyPress}
            onPaste={handlePaste}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder || 'Message'}
            disabled={isUploading}
            rows={1}
            className="flex-1 min-w-0 resize-none overflow-y-auto bg-transparent py-1.5 text-[13.5px] leading-5 text-gray-900 placeholder:text-gray-400 focus:outline-none disabled:opacity-50"
            style={{ minHeight: minTextareaHeight, maxHeight: maxTextareaHeight }}
          />
          <button
            onClick={() => imageInputRef.current?.click()}
            disabled={isUploading}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
            title="Add a photo"
          >
            <Image className="h-[18px] w-[18px]" />
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
            title="Attach a PDF or document"
          >
            <Paperclip className="h-[18px] w-[18px]" />
          </button>
          <button
            onClick={handleSend}
            disabled={!hasContent || isUploading}
            className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
              hasContent ? 'bg-gray-900 text-white hover:bg-black' : 'bg-gray-100 text-gray-300'
            }`}
            title="Send (Enter)"
          >
            {isUploading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            ) : (
              <Send className="h-4 w-4 -ml-px" />
            )}
          </button>
        </div>
        <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
        <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.txt,.xls,.xlsx" onChange={handleFileSelect} className="hidden" />
      </div>
    );
  }

  return (
    <>
      {/* GIF Picker Modal */}
      {showGifPicker && (
        <GifPicker
          onSelect={handleGifSelect}
          onClose={() => setShowGifPicker(false)}
        />
      )}

      <div className="space-y-3">
        {/* Image Preview */}
        {imagePreview && (
          <div className="relative inline-block">
            <img 
              src={imagePreview} 
              alt="Preview" 
              className="max-w-[200px] max-h-[200px] rounded-lg border border-gray-200"
            />
            <button
              onClick={removeImage}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center 
                         justify-center text-white hover:bg-red-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* GIF Preview */}
        {selectedGif && (
          <div className="relative inline-block">
            <img 
              src={selectedGif} 
              alt="GIF Preview" 
              className="max-w-[200px] max-h-[200px] rounded-lg border border-gray-200"
            />
            <button
              onClick={removeGif}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center 
                         justify-center text-white hover:bg-red-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="absolute bottom-2 right-2 px-2 py-1 bg-gray-800/80 backdrop-blur-sm rounded text-xs text-white">
              GIF
            </div>
          </div>
        )}

        {/* File Preview */}
        {selectedFile && (
          <div className="relative inline-block">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 max-w-[300px]">
              <FileText className="w-8 h-8 text-teal-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-gray-900 text-sm font-medium truncate">{selectedFile.name}</p>
                <p className="text-gray-500 text-xs">{formatFileSize(selectedFile.size)}</p>
              </div>
              <button
                onClick={removeFile}
                className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center 
                           text-white hover:bg-red-600 transition-colors flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

          {/* Input Area */}
        <div className={`flex items-end ${compact ? 'gap-2' : 'gap-3'}`}>
        <div 
          className={`flex-1 bg-gray-50 border rounded-xl overflow-hidden transition-all ${
            isDragging 
              ? 'border-teal-500 border-2 bg-teal-50' 
              : 'border-gray-200'
          }`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => handleTextChange(e.target.value)}
            onKeyPress={handleKeyPress}
            onPaste={handlePaste}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="Type a message... (Shift+Enter for new line, Ctrl+V to paste images, or drag & drop)"
            disabled={isUploading}
            rows={1}
            className={`w-full bg-transparent text-gray-900 placeholder-gray-400 
                       focus:outline-none disabled:opacity-50 resize-none
                       overflow-y-auto ${compact ? 'px-3 py-2.5 text-sm' : 'px-4 py-2.5'}`}
            style={{ minHeight: minTextareaHeight, maxHeight: maxTextareaHeight }}
          />
        </div>
        
        {/* Image Upload Button */}
        {!compact && (
          <button
            onClick={() => imageInputRef.current?.click()}
            disabled={isUploading}
            className="p-2.5 rounded-xl bg-gray-50 border border-gray-200 text-teal-600
                       hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            title="Upload Image"
          >
            <Image className="w-5 h-5" />
          </button>
        )}

        {/* GIF Picker Button */}
        {!compact && (
          <button
            onClick={() => setShowGifPicker(true)}
            disabled={isUploading}
            className="p-2.5 rounded-xl bg-gray-50 border border-gray-200 text-pink-500
                       hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            title="Send GIF"
          >
            <Sparkles className="w-5 h-5" />
          </button>
        )}

        {/* File Upload Button (PDFs, docs) - available in compact mode too */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className={`rounded-xl bg-gray-50 border border-gray-200 text-blue-500
                     hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all ${
                       compact ? 'p-2' : 'p-2.5'
                     }`}
          title="Attach PDF or document"
        >
          <Paperclip className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
        </button>

        {/* Send Button */}
        <button
          onClick={handleSend}
          disabled={(!text.trim() && !selectedImage && !selectedFile && !selectedGif) || isUploading}
          className={`rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500
                     text-white disabled:opacity-50 disabled:cursor-not-allowed
                     hover:shadow-lg hover:shadow-teal-500/20 transition-all ${
                       compact ? 'p-2' : 'p-2.5'
                     }`}
        >
          {isUploading ? (
            <div className={`border-2 border-white/30 border-t-white rounded-full animate-spin ${
              compact ? 'w-4 h-4' : 'w-5 h-5'
            }`} />
          ) : (
            <Send className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
          )}
        </button>

        {/* Hidden File Inputs */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          className="hidden"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.txt,.xls,.xlsx"
          onChange={handleFileSelect}
          className="hidden"
        />
        </div>
      </div>
    </>
  );
};

export default MessageInput;
